import moment from 'moment';
import { Injectable } from '@angular/core';
import { EMPTY, of, zip } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, take, takeWhile, withLatestFrom } from 'rxjs/operators';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { ActionService, TourService } from '../../services';
import { AddRequiredTrackedActions, AddRequiredTrackedActionsFail, CancelSwitchMachineUpdating, CompleteTourStopActions, DeleteDriverNote, DeleteDriverNoteFail, DeleteDriverNoteSuccess, FuelRefill, FuelRefillFail, FuelRefillSuccess, InitCompleteTourStopActions, InitLoadAction, LoadDriverData, LoadDriverDataFail, LoadDriverDataSuccess, LoadTourSingle, LoadTourSingleFail, LoadTourSingleSuccess, SearchMachine, SearchMachineFail, SearchMachineSuccess, SwitchMachine, SwitchMachineFail, SwitchMachineSuccess, TourActionTypes, UnloadMachinesAtRaster, UpsertDriverNote, UpsertDriverNoteFail, UpsertDriverNoteSuccess } from '../actions';
import { ActionType, Confirmation, ConfType, StatusType, Stop } from '@models/*';
import { AppState, RouterBack } from '@appstore/*';
import { OverviewState } from '../registration';
import { ensureTourStopTrackingStarted } from '../utils';
import { getAllNotesForCounterParty, getCurrentAction, getCurrentStop } from '../selectors';
import { ToastService } from '../../../core/services/toast.service';
import { ConfirmationService } from '../../../core/services/confirmation.service';
import { ComponentGeneratorService } from '../../../core/services/component-generator.service';
import { TransportPurpose } from '../../../base/entities/transport.interface';

@Injectable()
export class TourEffects {
    constructor(
        private actions$: Actions,
        private tourService: TourService,
        private actionService: ActionService,
        private componentGenerator: ComponentGeneratorService,
        private confirmations: ConfirmationService,
        private store: Store<OverviewState | AppState>,
        private toastService: ToastService,
    ) {
    }

    loadDriverData$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.LOAD_DRIVER_DATA),
        map((action: LoadDriverData) => action.payload),
        switchMap(({ date, performPreload, refreshMsg }) => {
            return this.tourService.getDriverData(date, refreshMsg).pipe(
                map(data => new LoadDriverDataSuccess({ ...data, performPreload })),
                catchError(error => of(new LoadDriverDataFail({ error }))),
            );
        }),
    ));

    loadTourSingle$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.LOAD_TOUR_SINGLE),
        map((action: LoadTourSingle) => action.payload),
        switchMap(tourId => {
            return this.tourService.getTour(tourId).pipe(
                map(tour => {
                    if (tour) {
                        return new LoadTourSingleSuccess(tour);
                    } else {
                        return new LoadTourSingleFail({ id: tourId, error: null });
                    }
                })
            );
        })
    ));

    /*
     * TODO: the effects can be simplified by merging the different "complete tour stop actions" into one NgRx action
     *  starting with InitCompleteTourStopActions
     *  instead of InitUnloadAtDepot and InitLoadAction and instead of calling CompleteTourStopAction directly from the components
     *  and taking either a list of actionId's (e.g. from selection of actions on the stop details page)
     *  or using the "currentAction" (e.g. when called from the action details page)
     */
    completeTourStopAction$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.INIT_COMPLETE_TOUR_STOP_ACTIONS),
        map((action: InitCompleteTourStopActions) => action.payload),
        ensureTourStopTrackingStarted(this),
        withLatestFrom(
            this.store.select(getCurrentStop),
            this.store.select(getCurrentAction),
        ),
        switchMap(([payload, currentStop, currentAction]) => {
            const { dialogInsertion } = payload;
            const { transport } = currentAction;
            if (transport && [TransportPurpose.SUBRENTAL, TransportPurpose.PURCHASE].includes(transport.purpose) && !transport.vendorId) {
                // Validation (performed here instead of in backend for the offline support)
                // For this action, the (un)loadAtVendor event will not be able to published due the missing vendor id
                // -> do not allow this (un)load action to complete.
                this.toastService.generateFailureToast(ConfType.COMPLETE_VENDOR_ACTION);
                return EMPTY;
            }
            const confirmationType = currentAction.actionType === ActionType.UNLOAD
                ? ConfType.STATUS_DELIVERED
                : ConfType.STATUS_IN_TRANSPORT;

            this.componentGenerator.generateConfirmationDialog(dialogInsertion, confirmationType);
            return this.confirmations.stream.pipe(
                takeWhile((conf: Confirmation<null>) => conf.type !== ConfType.CANCEL_CONFIRMATION),
                take(1),
                map((conf: Confirmation<null>) => {
                    return new CompleteTourStopActions({
                        updateData: [{
                            tourStopActionId: currentAction.id,
                            depotId: null,
                            rasterLocation: null,
                            vendorId: currentAction.transport.vendorId,
                            driverMessageForCounterParty: currentAction.driverMessageForCounterParty,
                        }],
                        confirmationType: conf.type,
                        timestamp: moment().toISOString(true),
                        dialogInsertion: payload.dialogInsertion,
                    });
                }),
            );
        }),
    ));

    loadTourStopAction$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.INIT_LOAD_ACTION),
        map((action: InitLoadAction) => action.payload),
        ensureTourStopTrackingStarted(this),
        withLatestFrom(
            this.store.select(getCurrentStop),
            this.store.select(getAllNotesForCounterParty),
        ),
        switchMap(([payload, currentStop, notesForCounterParty]: [any, Stop, { [key: number]: string }]) => {
            const { actionIds, dialogInsertion } = payload;
            this.componentGenerator.generateConfirmationDialog(
                dialogInsertion,
                ConfType.STATUS_IN_TRANSPORT,
                { count: actionIds.length },
            );

            return this.confirmations.stream.pipe(
                takeWhile((conf: Confirmation<null>) => conf.type !== ConfType.CANCEL_CONFIRMATION),
                filter((conf: Confirmation<null>) => conf.type === ConfType.STATUS_IN_TRANSPORT),
                take(1),
                map(conf => {
                    const actionUpdateObjects = actionIds.map(id => {
                        const currentAction = currentStop.actions.find(action => action.id === id);
                        return {
                            tourStopActionId: id,
                            depotId: currentStop.depotId,
                            rasterLocation: currentAction.machineRasterLocation || null,
                            vendorId: null,
                            driverMessageForCounterParty: notesForCounterParty[id],
                        };
                    });

                    return new CompleteTourStopActions({
                        updateData: actionUpdateObjects,
                        confirmationType: conf.type,
                        timestamp: moment().toISOString(true),
                        dialogInsertion,
                    });
                }),
            );
        }),
    ));

    completeTourStopActions$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.COMPLETE_TOUR_STOP_ACTIONS),
        map((action: CompleteTourStopActions) => action.payload),
        concatMap(payload => {
            const completeTSAsPayload = payload.updateData.map(elem => ({ ...elem, status: StatusType.COMPLETED }));

            return this.actionService.completeTourStopActions(completeTSAsPayload, payload.confirmationType).pipe(
                map(res => {
                    if (res) {
                        const tsaUpdates = completeTSAsPayload.map(elem => {
                            return {
                                tourStopActionId: elem.tourStopActionId,
                                status: elem.status,
                                depotId: elem.depotId,
                                rasterLocation: elem.rasterLocation,
                                vendorId: !!elem.vendorId ? elem.vendorId : null,
                                driverMessageForCounterParty: elem.driverMessageForCounterParty,
                            };
                        });
                        return new AddRequiredTrackedActions(tsaUpdates);
                    } else {
                        const tourStopActionIds = completeTSAsPayload.map(elem => elem.tourStopActionId);

                        return new AddRequiredTrackedActionsFail({ tourStopActionIds, error: null });
                    }
                })
            );
        })
    ));

    upsertDriverNote$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.UPSERT_DRIVER_NOTE),
        map((action: UpsertDriverNote) => action.payload),
        switchMap(payload => {
            const httpPayload = {
                tourStopActionId: payload.tourStopActionId,
                note: payload.driverNote.note,
                timestamp: payload.driverNote.timestamp,
            };

            return this.actionService.upsertDriverNote(httpPayload).pipe(
                map(res => {
                    if (res) {
                        return new UpsertDriverNoteSuccess(payload);
                    } else {

                        return new UpsertDriverNoteFail({ ...payload, error: null });
                    }
                })
            );
        })
    ));

    deleteDriverNote$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.DELETE_DRIVER_NOTE),
        map((action: DeleteDriverNote) => action.payload),
        switchMap(payload => {
            const deletePayload = { tourStopActionId: payload.tourStopActionId };
            return this.actionService.deleteDriverNote(deletePayload).pipe(
                map(res => {
                    if (res) {
                        return new DeleteDriverNoteSuccess(payload);
                    } else {

                        return new DeleteDriverNoteFail({ ...payload, error: null });
                    }
                })
            );
        })
    ));

    unloadMachinesAtRaster$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.UNLOAD_MACHINES_AT_RASTER),
        map((action: UnloadMachinesAtRaster) => action.payload),
        concatMap(payload => {
            const completeTsasPayload = payload.map(elem => ({ ...elem, status: StatusType.COMPLETED }));
            return zip(
                this.actionService.completeTourStopActions(completeTsasPayload, ConfType.STATUS_DELIVERED),
                of(completeTsasPayload)
            );
        }),
        concatMap(([res, completeTsasPayload]) => {
            const actionsArray: any[] = [];
            if (res) {
                const tsaUpdates = completeTsasPayload.map(elem => {
                    return {
                        tourStopActionId: elem.tourStopActionId,
                        status: elem.status,
                        rasterLocation: elem.rasterLocation
                    };
                });
                actionsArray.push(new AddRequiredTrackedActions(tsaUpdates));
                actionsArray.push(new RouterBack());
            } else {
                const tourStopActionIds = completeTsasPayload.map(elem => elem.tourStopActionId);

                actionsArray.push(new AddRequiredTrackedActionsFail({ tourStopActionIds, error: null }));
            }

            return actionsArray;
        })
    ));

    fuelRefill$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.FUEL_REFILL),
        map((action: FuelRefill) => action.payload),
        switchMap((payload) => {
            return this.actionService.fuelRefill(payload).pipe(
                map(res => {
                    if (res) {
                        const successPayload = {
                            tourStopActionId: payload.tourStopActionId,
                            amount: payload.amount,
                            time: moment().toISOString(true)
                        };
                        return new FuelRefillSuccess(successPayload);
                    } else {
                        return new FuelRefillFail({tourStopActionId: payload.tourStopActionId, error: null });
                    }
                })
            );
        })
    ));

    searchMachine$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.SEARCH_MACHINE),
        map((action: SearchMachine) => action.payload),
        switchMap(payload => {
            return this.actionService.searchMachine(payload).pipe(
                map(res => {
                    if (res) {
                        return new SearchMachineSuccess(res);
                    } else {
                        return new SearchMachineFail({ error: null });
                    }
                })
            );
        })
    ));

    switchMachine$ = createEffect(() => this.actions$.pipe(
        ofType(TourActionTypes.SWITCH_MACHINE),
        map((action: SwitchMachine) => action.payload),
        ensureTourStopTrackingStarted(this,
             payload => this.store.dispatch(new CancelSwitchMachineUpdating({ contractDetailId: payload.contractDetailId }))),
        switchMap(payload => {
            return this.actionService.switchMachine(payload).pipe(
                map((res: any) => {
                    if (res) {
                        return new SwitchMachineSuccess({
                            newFleetNumber: payload.newFleetNumber,
                            contractDetailId: payload.contractDetailId,
                            oldMachineInternalId: payload.oldMachineInternalId,
                            swapProposal: res.swapProposal || null
                        });
                    } else {
                        return new SwitchMachineFail({
                            contractDetailId: payload.contractDetailId,
                            error: null,
                        });
                    }
                })
            );
        })
    ));
}
